import { useEffect } from "react";
import styles from "./dashboard.module.scss";
import { connect } from "react-redux";
import {
  getPOMDashboardSummaryRequest,
  getPOMDashboardDetailRequest,
  clearPOMDashboardData,
} from "../../store/actions/general.actions";
import {
  pomDashboardSummary,
  pomDashboardDetail,
  pomDashboardLoading,
  pomDashboardDetailLoading,
} from "../../store/selectors/general.selector";
import Portfolios from "../portfolio/portfolios";
import RiskCard from "./components/PortfolioManagerDashboard/RiskCard";
import ProgressCard from "./components/PortfolioManagerDashboard/ProgressCard";
import SimpleCard from "./components/PortfolioManagerDashboard/SimpleCard";
import PortfolioValuePanel from "./components/PortfolioManagerDashboard/PortfolioValuePanel";
import ResourceCapacityPanel from "./components/PortfolioManagerDashboard/ResourceCapacityPanel";
import { RiskMap } from "../../components/charts/DashboardCharts";
import pmStyles from "./styles/portfolioManagerDashboard.module.scss";
import Programs from "../program/programs";
import BudgetCostCard from "./components/PortfolioManagerDashboard/CostsCard";
import { DashboardSkeleton } from "./components/PortfolioManagerDashboard/DashboardSkeletons";
import PortfolioProjectsDonut from "./components/PortfolioManagerDashboard/PortfolioProjectsDonut";
import PortfoliosPanel from "./components/PortfolioManagerDashboard/PortfoliosPanel";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function buildCapacityData(rc) {
  if (!rc) return [];
  const { planned = [], logged = [] } = rc;
  return MONTH_NAMES.map((name, i) => {
    const m = i + 1;
    return {
      month: name,
      planned: planned.find((d) => d.month === m)?.value ?? 0,
      actual: logged.find((d) => d.month === m)?.value ?? 0,
    };
  });
}


function buildValueChartData(valueDelivered = [], budgetBurnRate = []) {
  return MONTH_NAMES.map((name, i) => {
    const m = i + 1;
    const vd = valueDelivered.find((d) => d.month === m);
    const br = budgetBurnRate.find((d) => d.month === m);
    return {
      label: name,
      planned: vd?.planned ?? 0,
      actual: vd?.actual ?? 0,
      burnRate: br?.burnRatePercentage ?? 0,
      monthlySpend: br?.monthlySpend ?? 0,
    };
  });
}

const STATUS_MAP = [
  { name: "On Target", color: "#0d9f61" },
  { name: "Monitoring", color: "#eab308" },
  { name: "Slipping", color: "#dc2626" },
  { name: "Completed", color: "#342c82" },
  { name: "On Hold", color: "#9e9e9e" },
];

function buildStatusSegments(projectsByStatus = []) {
  const total = projectsByStatus.reduce((sum, s) => sum + s.count, 0);
  if (!total) {
    //fallback
    return STATUS_MAP.map((s) => ({ name: s.name, color: s.color, range: 100 / STATUS_MAP.length }));
  }
  return STATUS_MAP
    .map((meta, i) => {
      const found = projectsByStatus.find((s) => s.status === i);
      return { name: meta.name, color: meta.color, value: (found?.count ?? 0), range: Math.round(((found?.count ?? 0) / total) * 100) };
    })
    .filter((s) => s.range > 0);
}


const PortfolioManagerDashboard = (props) => {
  const {
    PortfolioId,
    setPortfolioId,
    setPortfolioName,
    getPOMDashboardSummaryRequest,
    getPOMDashboardDetailRequest,
    clearPOMDashboardData,
    summary,
    isLoading,
  } = props;

  useEffect(() => {
    if (PortfolioId) {
      clearPOMDashboardData();
      getPOMDashboardDetailRequest(PortfolioId);
    } else {
      getPOMDashboardSummaryRequest();
    }
  }, [PortfolioId]);

  if (isLoading) {
    return (
      <div className={`mt-4 ${styles.SummaryPanel}`}>
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <>
      <div className={`mt-4 ${styles.SummaryPanel}`}>
        <div className="flex gap-3 w-full mb-3 ">
          <RiskCard
            value={summary?.projectsAtRisk?.current ?? 0}
            trend={(summary?.projectsAtRisk?.current ?? 0) - (summary?.projectsAtRisk?.previous ?? 0)}
            trendLabel="vs last period"
          />
          <ProgressCard
            title="Overall Progress"
            value={summary?.overallProgress ?? 0}
          />
          <SimpleCard
            title="Active Projects"
            value={summary?.activeProjects ?? 0}
            secondaryValue={summary?.totalProjects ?? 0}
          />
          <SimpleCard
            title="On-Time Delivery"
            value={`${Math.round((summary?.onTimeDelivery ?? 0) * 100)}%`}
          />
          <BudgetCostCard
            budgetChartData={summary?.costsSummary}
          />
        </div>
        {!PortfolioId && summary?.flaggedPortfolios?.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <PortfoliosPanel
                portfolios={summary.flaggedPortfolios}
                onPortfolioClick={(portfolio) => {
                  setPortfolioId(portfolio.id);
                  setPortfolioName(portfolio.name);
                }}
                loading={isLoading}
              />
            </div>
          </div>
        )}
        <div className="row align-items-stretch">
          <div className="col-6 mb-3">
            <PortfolioValuePanel
              gaugeSegments={buildStatusSegments(summary?.projectsByStatus)}
              chartData={buildValueChartData(summary?.valueDelivered, summary?.budgetBurnRate)}
            />
          </div>
          <div className="col-3 mb-3">
            <PortfolioProjectsDonut
              gaugeSegments={buildStatusSegments(summary?.projectsByStatus)}
            />
          </div>
          <div className="col-3 mb-3">
            <div className={pmStyles.analyticsPanel}>
              <div className={pmStyles.panelHeader}>
                <span className={pmStyles.label}>Risk Heat Map</span>
              </div>
              <div style={{ position: "relative", width: "100%", height: "300px" }}>
                <RiskMap riskData={summary?.risks ?? []} />
              </div>
            </div>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-12 mb-3 d-flex flex-column" style={{ height: "400px" }}>
            <ResourceCapacityPanel data={buildCapacityData(summary?.resourceCapacity)} />
          </div>
          {/* <div className="col-7 mb-3 portfolio-table" style={{ height: "400px", overflow: "auto" }}>
            {PortfolioId ? (
              <Programs
                portfolioID={PortfolioId}
                overviewType="Dashboard"
              />
            ) : (
              <Portfolios
                overviewType="Dashboard"
                dashboardFilterSetId={setPortfolioId}
                dashboardFilterSetName={setPortfolioName}
                loadingSkeleton={isLoading}
                dashboardFilterId={PortfolioId}
              />
            )}
          </div> */}
        </div>
      </div>
    </>
  );
};

const mapDispatchToProps = {
  getPOMDashboardSummaryRequest,
  getPOMDashboardDetailRequest,
  clearPOMDashboardData,
};

const mapStateToProps = (state, ownProps) => {
  const toPlain = (val) => (val?.toJS ? val.toJS() : val) ?? {};
  const summary = toPlain(pomDashboardSummary(state));
  const detail = toPlain(pomDashboardDetail(state));
  const fetchLoading = ownProps.PortfolioId
    ? pomDashboardDetailLoading(state)
    : pomDashboardLoading(state);

  if (ownProps.PortfolioId) {
    const resolvedSummary = Object.keys(detail).length > 0 ? detail : summary;
    const isLoading = fetchLoading || Object.keys(resolvedSummary).length === 0;
    return { isLoading, summary: resolvedSummary };
  } else {
    const isLoading = fetchLoading || Object.keys(summary).length === 0;
    return { isLoading, summary };
  }
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PortfolioManagerDashboard);
